import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChartLineUp, ArrowSquareOut, Clock } from "@phosphor-icons/react";
import { fetchInsight } from "../lib/api";
import { relativeTime } from "../lib/time";
import SEO from "../components/SEO";

const tilt = (s) => (s >= 60 ? "BULLISH" : s <= 40 ? "BEARISH" : "NEUTRAL");
const tiltColor = (s) =>
  s == null ? "#A1A1AA" : s >= 60 ? "#34C759" : s <= 40 ? "#FF3B30" : "#FFCC00";

// Minimal Markdown → JSX. Handles paragraphs, bold (**), italics (_), and
// inline link [text](url). Not a full parser — by design, since the LLM
// prompt restricts output to plain prose + occasional lists.
function renderMarkdown(md) {
  if (!md) return null;
  const blocks = md.split(/\n{2,}/);
  return blocks.map((block, i) => {
    // Bullet list
    if (/^\s*[-*]\s+/.test(block)) {
      const items = block.split(/\n/).map((l) => l.replace(/^\s*[-*]\s+/, "")).filter(Boolean);
      return (
        <ul key={i} className="list-disc pl-5 my-4 space-y-1 text-white/85">
          {items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ul>
      );
    }
    return (
      <p key={i} className="my-4 text-white/85 leading-relaxed text-[15px]">
        {renderInline(block)}
      </p>
    );
  });
}

function renderInline(s) {
  // Bold **x** then italic _x_ then [text](url)
  const parts = [];
  let i = 0;
  const re = /(\*\*[^*]+\*\*|_[^_]+_|\[[^\]]+\]\([^)]+\))/g;
  let m;
  let last = 0;
  while ((m = re.exec(s))) {
    if (m.index > last) parts.push(s.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) parts.push(<strong key={i++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("_")) parts.push(<em key={i++}>{tok.slice(1, -1)}</em>);
    else {
      const lm = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (lm) parts.push(
        <a key={i++} href={lm[2]} target="_blank" rel="noopener noreferrer" className="text-neutral_zone underline underline-offset-2 hover:text-yellow-300">
          {lm[1]}
        </a>
      );
    }
    last = m.index + tok.length;
  }
  if (last < s.length) parts.push(s.slice(last));
  return parts;
}

export default function InsightDetail() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    fetchInsight(slug)
      .then((d) => setPost(d))
      .catch((e) => setErr(e?.response?.status === 404 ? "not_found" : "error"))
      .finally(() => setLoading(false));
  }, [slug]);

  const sentColor = tiltColor(post?.sentiment_score);
  const sentLabel = post?.sentiment_score != null ? tilt(post.sentiment_score) : "—";

  const ogImage = `https://sent-i-meter.com/api/og/insight/${encodeURIComponent(slug)}.png`;

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="insight-detail-page">
      <SEO
        title={post?.title || "Market Briefing"}
        path={`/insights/${encodeURIComponent(slug)}`}
        description={post?.tldr || "AI-generated daily market briefing on Sent-i-meter."}
        keywords={(post?.tags || []).concat([
          post?.region ? `${post.region} markets` : null,
          "market briefing",
          "AI market analysis",
          post?.date,
        ].filter(Boolean))}
        image={ogImage}
        jsonLd={post ? {
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: post.title,
          description: post.tldr,
          datePublished: post.created_at || post.date,
          dateModified: post.created_at || post.date,
          author: { "@type": "Organization", name: "Sent-i-meter" },
          publisher: {
            "@type": "Organization",
            name: "Sent-i-meter",
            logo: { "@type": "ImageObject", url: "https://sent-i-meter.com/icon-512.png" },
          },
          image: [ogImage],
          articleSection: post.region,
          keywords: (post.tags || []).join(", "),
          mainEntityOfPage: { "@type": "WebPage", "@id": `https://sent-i-meter.com/insights/${slug}` },
        } : null}
      />

      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10 px-4 md:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/insights")} className="flex items-center gap-2 text-white/70 hover:text-white" data-testid="insight-back">
            <ArrowLeft size={16} weight="bold" />
            <span className="overline">all briefings</span>
          </button>
          <div className="w-px h-6 bg-white/10 mx-2" />
          <button onClick={() => navigate("/")} className="w-8 h-8 bg-white text-black flex items-center justify-center">
            <ChartLineUp size={16} weight="bold" />
          </button>
        </div>
      </header>

      <main className="px-4 md:px-6 py-6 max-w-[760px] mx-auto">
        {loading && <div className="overline py-12 text-center">loading briefing…</div>}

        {err === "not_found" && (
          <div className="bg-surface border border-white/10 p-8 text-center" data-testid="insight-not-found">
            <div className="overline mb-2">briefing not found</div>
            <p className="text-sm text-white/70 mb-4">This briefing may have been removed or the link is malformed.</p>
            <button onClick={() => navigate("/insights")} className="px-3 py-1.5 bg-white text-black mono text-[10px] uppercase tracking-[0.18em]">
              See all briefings
            </button>
          </div>
        )}

        {err === "error" && (
          <div className="bg-surface border border-bearish/30 p-8 text-center">
            <p className="text-sm text-bearish">Failed to load briefing. Try again later.</p>
          </div>
        )}

        {post && (
          <article data-testid="insight-article">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-2 h-2" style={{ background: sentColor }} />
              <span className="mono text-[10px] uppercase tracking-[0.16em]" style={{ color: sentColor }}>
                {post.region} · {sentLabel}
              </span>
              <span className="overline ml-auto flex items-center gap-1">
                <Clock size={11} weight="bold" />
                {relativeTime(post.created_at || post.date)}
              </span>
            </div>

            <h1 className="heading text-3xl md:text-4xl font-bold tracking-tight leading-tight" data-testid="insight-title">
              {post.title}
            </h1>

            {post.tldr && (
              <p className="mt-4 text-lg text-white/80 leading-relaxed border-l-2 pl-4" style={{ borderColor: sentColor }}>
                {post.tldr}
              </p>
            )}

            <div className="flex flex-wrap gap-2 mt-5 mb-2">
              {post.sentiment_score != null && (
                <span className="mono text-xs px-2 py-1 border" style={{ color: sentColor, borderColor: `${sentColor}55` }}>
                  Sentiment {Math.round(post.sentiment_score)}/100
                </span>
              )}
              {(post.tags || []).map((tag) => (
                <span key={tag} className="mono text-xs px-2 py-1 border border-white/10 text-white/70">
                  {tag}
                </span>
              ))}
            </div>

            <div className="prose prose-invert max-w-none mt-6" data-testid="insight-body">
              {renderMarkdown(post.body_md)}
            </div>

            {(post.top_headlines || []).length > 0 && (
              <section className="mt-10 border-t border-white/10 pt-6" data-testid="insight-sources">
                <div className="overline mb-3">grounded in these headlines</div>
                <ul className="space-y-2">
                  {post.top_headlines.map((h, i) => (
                    <li key={i} className="text-sm">
                      <a href={h.link} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white inline-flex items-start gap-1.5">
                        <ArrowSquareOut size={12} weight="bold" className="mt-1 text-white/40" />
                        <span>
                          {h.title}
                          {h.source && <span className="text-white/40 mono text-xs ml-2">{h.source}</span>}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <footer className="mt-12 border-t border-white/10 pt-4 flex items-center justify-between">
              <button onClick={() => navigate("/insights")} className="overline hover:text-white">
                ← all briefings
              </button>
              <span className="overline">ai-written · neutral · not financial advice</span>
            </footer>
          </article>
        )}
      </main>
    </div>
  );
}
